import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FiSave, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import { fetchScreenHiddenColumns, saveScreenHiddenColumns } from "../libs/screenColumnPreference";
import "../css/TableColumnSettingsModal.css";

/**
 * Popup cài đặt ẩn/hiện cột — lưu theo user + màn hình trên server.
 */
export default function TableColumnSettingsModal({
  show,
  onClose,
  screenKey,
  columns,
  allColumns,
  hiddenColumns,
  onSaved,
}) {
  const [draftVisible, setDraftVisible] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    const hidden = new Set(hiddenColumns || []);
    const next = {};
    for (const col of columns) {
      next[col.key] = !hidden.has(col.key);
    }
    setDraftVisible(next);
  }, [show, hiddenColumns, columns]);

  const handleToggle = (key, checked) => {
    setDraftVisible((prev) => ({ ...prev, [key]: checked }));
  };

  const handleSave = async () => {
    const draftKeys = new Set(columns.map((c) => c.key));
    const hiddenFromDraft = columns.filter((c) => !draftVisible[c.key]).map((c) => c.key);
    const preserved = (hiddenColumns || []).filter((k) => !draftKeys.has(k));
    const hidden = [...preserved, ...hiddenFromDraft];
    setSaving(true);
    try {
      const result = await saveScreenHiddenColumns(
        axiosInstance,
        screenKey,
        hidden,
        allColumns?.length ? allColumns : columns
      );
      if (!result?.success) {
        toast.error(result?.message || "Không lưu được cài đặt cột.");
        return;
      }
      onSaved?.(result.data || hidden);
      toast.success(result.message || "Đã lưu cài đặt cột.");
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Không lưu được cài đặt cột.");
    } finally {
      setSaving(false);
    }
  };

  const useTwoColumns = columns.length >= 4;
  const splitAt = Math.ceil(columns.length / 2);
  const columnGroups = useTwoColumns
    ? [columns.slice(0, splitAt), columns.slice(splitAt)]
    : [columns];

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size={useTwoColumns ? "lg" : "md"}
      dialogClassName={`table-column-settings-modal${
        useTwoColumns ? " table-column-settings-modal--wide" : ""
      }`}
    >
      <Modal.Header closeButton className="py-2">
        <Modal.Title>Cài đặt hiển thị cột</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted col-settings-hint mb-2">
          Bỏ chọn cột để ẩn khỏi bảng. Cài đặt được lưu theo tài khoản của bạn.
        </p>
        <div className={useTwoColumns ? "col-settings-columns" : "col-settings-list"}>
          {columnGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="col-settings-list">
              {group.map((col) => (
                <Form.Check
                  key={col.key}
                  type="checkbox"
                  id={`col-setting-${screenKey}-${col.key}`}
                  label={col.label}
                  checked={!!draftVisible[col.key]}
                  onChange={(e) => handleToggle(col.key, e.target.checked)}
                />
              ))}
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer className="py-2">
        <Button variant="outline-secondary" size="sm" onClick={onClose} disabled={saving}>
          <FiX className="me-1" />
          Hủy
        </Button>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          <FiSave className="me-1" />
          {saving ? "Đang lưu..." : "Lưu"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
